import { useMemo } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Input } from '@/components/common';
import { cn } from '@/utils';
import type { SchemaToken } from '@/services/microsite';
import { humanize } from './sectionUtils';

// =============================================================================
// List-field shape hints
// -----------------------------------------------------------------------------
// The flat `schema` map only declares a field as "list" — it does NOT describe
// the item shape. These hints let the builder render a friendly per-item form
// for the known repeaters; anything not listed falls back to key inference from
// existing data (object rows) or a single-value text row (scalar list), which
// keeps the form forward-compatible with new list fields.
// =============================================================================
const SCALAR_LIST_FIELDS = new Set<string>(['insurer_grid.insurer_slugs']);
const OBJECT_LIST_ITEM_KEYS: Record<string, string[]> = {
  'usp_grid.items': ['icon', 'title', 'description'],
  'faq.items': ['question', 'answer'],
};

export function IconBtn({
  onClick,
  disabled,
  title,
  danger,
  active,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  danger?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={cn(
        'rounded-md p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-30',
        danger
          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
          : active
            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
            : 'text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700',
      )}
    >
      {children}
    </button>
  );
}

export function SchemaField({
  sectionType,
  sectionId,
  field,
  token,
  value,
  editable,
  onChange,
}: {
  sectionType: string;
  /** Scopes the control's DOM id, so two sections of the same type on one page
   *  do not both claim `hero-heading` and leave one label pointing at the
   *  other's input. */
  sectionId: string;
  field: string;
  token: SchemaToken;
  value: unknown;
  editable: boolean;
  onChange: (value: unknown) => void;
}) {
  const label = humanize(field);
  const controlId = `${sectionId}-${field}`;

  if (token === 'bool') {
    return (
      <label className="flex items-center gap-3" htmlFor={controlId}>
        <input
          id={controlId}
          type="checkbox"
          checked={Boolean(value)}
          disabled={!editable}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      </label>
    );
  }

  if (token === 'text') {
    return (
      <div>
        <label
          htmlFor={controlId}
          className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
        <textarea
          id={controlId}
          value={(value as string) ?? ''}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
        />
      </div>
    );
  }

  if (token === 'int') {
    return (
      <Input
        id={controlId}
        label={label}
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        disabled={!editable}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? undefined : Number(raw));
        }}
      />
    );
  }

  if (token === 'list') {
    return (
      <ListEditor
        sectionType={sectionType}
        sectionId={sectionId}
        field={field}
        label={label}
        value={Array.isArray(value) ? (value as unknown[]) : []}
        editable={editable}
        onChange={onChange}
      />
    );
  }

  if (token === 'dict') {
    // Image pickers and card-config blocks land here. An editable JSON textarea
    // keeps them authorable rather than silently dropped, and preserves the last
    // valid value when the author is mid-edit.
    return (
      <div>
        <label
          htmlFor={controlId}
          className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label} <span className="text-xs font-normal text-gray-400">(JSON)</span>
        </label>
        <textarea
          id={controlId}
          defaultValue={value ? JSON.stringify(value, null, 2) : ''}
          disabled={!editable}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            if (!raw) return onChange(undefined);
            try {
              onChange(JSON.parse(raw));
            } catch {
              /* keep last valid value on parse error */
            }
          }}
          rows={4}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
        />
      </div>
    );
  }

  // str / url
  return (
    <Input
      id={controlId}
      label={token === 'url' ? `${label} (URL)` : label}
      value={(value as string) ?? ''}
      disabled={!editable}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// =============================================================================
// List editor — scalar list (strings) or object list (per-key inputs)
// =============================================================================
function unionKeys(items: unknown[]): string[] {
  const keys: string[] = [];
  for (const it of items) {
    if (it && typeof it === 'object' && !Array.isArray(it)) {
      for (const k of Object.keys(it as Record<string, unknown>)) {
        if (!keys.includes(k)) keys.push(k);
      }
    }
  }
  return keys;
}

export function ListEditor({
  sectionType,
  sectionId,
  field,
  label,
  value,
  editable,
  onChange,
}: {
  sectionType: string;
  sectionId: string;
  field: string;
  label: string;
  value: unknown[];
  editable: boolean;
  onChange: (value: unknown[]) => void;
}) {
  const key = `${sectionType}.${field}`;

  const { isScalar, itemKeys } = useMemo(() => {
    if (SCALAR_LIST_FIELDS.has(key)) return { isScalar: true, itemKeys: [] as string[] };
    const hinted = OBJECT_LIST_ITEM_KEYS[key];
    if (hinted) return { isScalar: false, itemKeys: hinted };
    const derived = unionKeys(value);
    if (derived.length > 0) return { isScalar: false, itemKeys: derived };
    // Empty + no hint: default to a scalar string list (safe, forward compatible).
    const allStrings = value.every((v) => typeof v === 'string');
    return allStrings ? { isScalar: true, itemKeys: [] } : { isScalar: false, itemKeys: ['title'] };
  }, [key, value]);

  const addRow = () => onChange([...value, isScalar ? '' : {}]);
  const removeRow = (index: number) => onChange(value.filter((_, i) => i !== index));
  const setScalar = (index: number, v: string) =>
    onChange(value.map((row, i) => (i === index ? v : row)));
  const setObjectField = (index: number, k: string, v: string) =>
    onChange(
      value.map((row, i) => (i === index ? { ...(row as Record<string, unknown>), [k]: v } : row)),
    );

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        {editable && (
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            <PlusIcon className="h-3.5 w-3.5" /> Add item
          </button>
        )}
      </div>

      {value.length === 0 && <p className="text-xs text-gray-400 dark:text-gray-500">No items yet.</p>}

      <div className="space-y-3">
        {value.map((row, index) => (
          <div
            key={index}
            className="flex items-start gap-2 rounded-md bg-gray-50 p-2 dark:bg-gray-800/60"
          >
            <div className="flex-1 space-y-2">
              {isScalar ? (
                <Input
                  id={`${sectionId}-${field}-${index}`}
                  aria-label={`${label} ${index + 1}`}
                  value={(row as string) ?? ''}
                  disabled={!editable}
                  onChange={(e) => setScalar(index, e.target.value)}
                />
              ) : (
                itemKeys.map((k) => (
                  <Input
                    key={k}
                    id={`${sectionId}-${field}-${index}-${k}`}
                    label={humanize(k)}
                    value={((row as Record<string, unknown>)?.[k] as string) ?? ''}
                    disabled={!editable}
                    onChange={(e) => setObjectField(index, k, e.target.value)}
                  />
                ))
              )}
            </div>
            {editable && (
              <IconBtn onClick={() => removeRow(index)} title="Remove item" danger>
                <TrashIcon className="h-4 w-4" />
              </IconBtn>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
